import { type FC, type ReactNode, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { ComplianceRecordKindType } from "@/types/graphql/partner.generated";
import {
  useComplianceRecordsQuery,
  useCreateComplianceRecordMutation,
  useUpdateComplianceRecordMutation,
  useDeleteComplianceRecordMutation,
} from "@/partner/entities/compliance";
import {
  ComplianceRecordsList,
  type ComplianceRecord,
} from "./ComplianceRecordsList";
import {
  ComplianceRecordFormDialog,
  type ComplianceRecordFormValues,
} from "./ComplianceRecordFormDialog";

interface ComplianceScreenProps {
  contentId?: string | null;
  productId?: string | null;
  onClose: () => void;
  /** Вкладки карточки (VideoTabsMenu / ProductTabsMenu). */
  menu?: ReactNode;
}

/**
 * Вкладка «Записи 2257» карточки контента или продукта. Видна только при
 * Platform.isComplianceEnabled: гейт стоит в самих вкладках
 * (VideoTabsMenu / ProductTabsMenu), сюда попадают уже с включённым
 * флагом.
 */
export const ComplianceScreen: FC<ComplianceScreenProps> = ({
  contentId,
  productId,
  onClose,
  menu,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ComplianceRecord | null>(null);

  const variables = {
    contentId: contentId ?? null,
    productId: productId ?? null,
  };

  // network-only: ссылка на файл подписана на 15 минут, из кэша она может
  // приехать уже протухшей.
  const { data, loading, refetch } = useComplianceRecordsQuery({
    variables,
    fetchPolicy: "network-only",
  });

  const reload = () => {
    // Ошибка перезапроса приезжает в data/error самого хука, отдельного
    // сообщения не показываем.
    refetch().catch(() => undefined);
  };

  const notifyError = (message: string) =>
    enqueueSnackbar(message, { variant: "error" });

  const [createRecord, { loading: creating }] =
    useCreateComplianceRecordMutation({
      onCompleted: () => {
        enqueueSnackbar(t("compliance.created"), { variant: "success" });
        setDialogOpen(false);
        reload();
      },
      onError: (error) => notifyError(error.message),
    });

  const [updateRecord, { loading: updating }] =
    useUpdateComplianceRecordMutation({
      onCompleted: () => {
        enqueueSnackbar(t("compliance.updated"), { variant: "success" });
        setDialogOpen(false);
        reload();
      },
      onError: (error) => notifyError(error.message),
    });

  const [deleteRecord] = useDeleteComplianceRecordMutation({
    onCompleted: () => {
      enqueueSnackbar(t("compliance.deleted"), { variant: "success" });
      reload();
    },
    onError: (error) => notifyError(error.message),
  });

  const handleSubmit = async (values: ComplianceRecordFormValues) => {
    if (editing) {
      await updateRecord({
        variables: {
          id: editing.id,
          input: {
            url:
              values.kind === ComplianceRecordKindType.LinkType
                ? values.url
                : null,
            performerName: values.performerName || null,
            note: values.note || null,
          },
        },
      });
      return;
    }

    await createRecord({
      variables: {
        input: {
          contentId: contentId ?? null,
          productId: productId ?? null,
          kind: values.kind,
          // apollo-upload-client сериализует File в multipart-запрос,
          // ключ передаётся как есть в Upload-скаляр.
          file:
            values.kind === ComplianceRecordKindType.FileType
              ? values.file
              : null,
          url:
            values.kind === ComplianceRecordKindType.LinkType
              ? values.url
              : null,
          performerName: values.performerName || null,
          note: values.note || null,
        },
      },
    });
  };

  const handleDelete = async (record: ComplianceRecord) => {
    if (!window.confirm(t("compliance.confirmDelete"))) return;
    await deleteRecord({ variables: { id: record.id } });
  };

  return (
    <Box>
      {/* Шапка повторяет форму видео и экран меток таймлайна: экран живёт
          во вкладке того же полноэкранного диалога, и крестик, заголовок и
          прилипание должны стоять на тех же местах, иначе при переключении
          вкладок шапка прыгает. */}
      <AppBar color="default" position="fixed" elevation={0}>
        <Toolbar>
          <IconButton edge="start" onClick={onClose} sx={{ mr: 1 }}>
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flex: 1, mr: 1 }}>
            {t("compliance.title")}
          </Typography>
        </Toolbar>
        <Divider />
      </AppBar>

      {/* Отступ под прилипшую шапку тот же, что в форме видео. */}
      <Box sx={{ mt: { xs: "56px", sm: "64px" } }}>{menu}</Box>

      <Card sx={{ m: 2, p: 2 }}>
        <Stack spacing={2}>
          <Button
            variant="contained"
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
            sx={{ alignSelf: "flex-start" }}
          >
            {t("compliance.add")}
          </Button>

          <ComplianceRecordsList
            records={data?.complianceRecords ?? []}
            loading={loading}
            onEdit={(record) => {
              setEditing(record);
              setDialogOpen(true);
            }}
            onDelete={handleDelete}
          />
        </Stack>
      </Card>

      <ComplianceRecordFormDialog
        open={dialogOpen}
        record={editing}
        saving={creating || updating}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
      />
    </Box>
  );
};
